import { uniq, xor } from 'lodash-es';
import { noOperation } from '../function';
import { ReadOnlyArray } from './ReadOnlyArray';

export { orderBy } from 'lodash-es';

export function arrayify<T>(
  data: T | readonly T[] | undefined | null,
  allowNull: boolean = false,
): readonly T[] {
  if (Array.isArray(data)) {
    return data;
  }

  if (typeof data === 'undefined') {
    return ReadOnlyArray.Empty as readonly T[];
  }

  if (data === null && !allowNull) {
    return ReadOnlyArray.Empty as readonly T[];
  }

  return [data] as readonly T[];
}

export function isEqualByEntries<T>(
  left: readonly T[],
  right: readonly T[],
): boolean {
  return xor(left, right).length === 0;
}

export function orderedFind<T, U = undefined>(
  source: readonly T[] | undefined,
  predicate: (item: T, toFind: U) => boolean,
  ...toMatch: U[]
): T | undefined {
  // Normalize the source to a non-undefined read-only array
  const items: readonly T[] = Array.isArray(source) ? source : ReadOnlyArray.Empty;

  // If no values to match are provided, we default to a single `undefined` value
  const searchValues: U[] = toMatch.length > 0 ? toMatch : [undefined as U];

  // Ensure we always have a function to call
  const matcher: (item: T, toFind: U) => boolean =
    typeof predicate === 'function'
      ? predicate
      : (noOperation as (item: T, toFind: U) => boolean);

  // Try each search value in order, returning the first matching item
  for (const valueToFind of searchValues) {
    const match = items.find((item) => matcher(item, valueToFind));
    if (match !== undefined) {
      return match;
    }
  }

  // If nothing matched, return undefined
  return undefined;
}

export function unique<T>(array: readonly T[]): T[] {
  return uniq(array);
}

export function includes<T>(
  sourceArray: readonly T[],
  toTestForArray: readonly T[],
): boolean {
  return toTestForArray.every((i) => sourceArray.includes(i));
}

export function getCyclicIndex<T>(array: readonly T[], index: number): number {
  const cyclicIndex =
    index < 0 ? array.length - 1 : index >= array.length ? 0 : index;
  return cyclicIndex;
}
